import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import apiInstance from '../../utils/axios';
import { 
  Facebook, 
  Instagram, 
  Shield,
  Mail,
  Phone,
  MapPin,
  Truck,
  CreditCard,
  Award,
  Headphones,
  AlertTriangle,
} from 'lucide-react';

const StoreFooter = () => {
  const { t, currentLanguage } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [storeInfo, setStoreInfo] = useState({
    store_name: 'ASMAR SHOP',
    phones: [],
    emails: ['contato@asmarshop.com.br'],
    whatsapp: '595981123456',
    address: 'Asunción, Paraguay',
    city: 'Asunción',
    country: 'Paraguay',
    facebook: '#',
    instagram: 'https://www.instagram.com/atacado_asmar/',
    tiktok: 'https://www.tiktok.com/@atacadoasmar?_r=1&_t=ZS-94FvDAKlN5u',
    footer_text: t('footerText') || 'Sua loja online de confiança com os melhores produtos e preços imbatíveis.',
  });

  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const response = await apiInstance.get('store-info/');
        setStoreInfo(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.log('Using default store info');
      }
    };
    fetchStoreInfo();
  }, []);

  const getPhones = () => {
    try {
      return Array.isArray(storeInfo.phones) 
        ? storeInfo.phones 
        : JSON.parse(storeInfo.phones);
    } catch {
      return [storeInfo.phones];
    }
  };

  const getEmails = () => {
    try {
      return Array.isArray(storeInfo.emails) 
        ? storeInfo.emails 
        : JSON.parse(storeInfo.emails);
    } catch {
      return [storeInfo.emails];
    }
  };

  return (
    <footer className="bg-card border-t border-border mt-8">
      <div className="mx-auto max-w-7xl px-4 py-8 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          {/* About */}
          <div>
            <h3 className="font-bold text-foreground mb-4">{t('aboutUs')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('contact')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('faq')}
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('support24')}
                </Link>
              </li>
            </ul>

            {/* Social Media */}
            <div className="flex gap-3 mt-5">
              <a href="https://www.instagram.com/atacado_asmar/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.tiktok.com/@atacadoasmar?_r=1&_t=ZS-94FvDAKlN5u" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <Link to="/vendedores/" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </Link>
            </div>
          </div>

          {/* Help */}
          <div>
            <h3 className="font-bold text-foreground mb-4">{t('navigation')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/products" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('products')}
                </Link>
              </li>
              <li>
                <Link to="/products?promotion=true" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('offers')}
                </Link>
              </li>
              <li>
                <Link to="/cart" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('cart')}
                </Link>
              </li>
              <li>
                <Link to="/por-mayor" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t('wholesale')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Delivery Notice */}
          <div>
            <h3 className="font-bold text-foreground mb-4">{t('deliveryInfo')}</h3>
            <div className="flex items-start gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                {t('brazilPickupNotice')}
              </p>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4" />
                <span>{t('fastDelivery')}</span>
              </div>
              <p className="text-xs pl-6">{t('fastDeliveryDesc')}</p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-border mt-8 pt-6">
          <div className="flex flex-wrap gap-6 mb-6 justify-center md:justify-start">
            {getPhones().length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4" />
                <span>{getPhones()[0]}</span>
              </div>
            )}
            {getEmails().length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                <span>{getEmails()[0]}</span>
              </div>
            )}
            {storeInfo.address && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{storeInfo.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-6">
          {/* Social Media Icons - Always Visible */}
          <div className="flex justify-center gap-4 mb-6">
            <a 
              href="https://www.instagram.com/atacado_asmar/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white flex items-center justify-center hover:opacity-90 transition-all hover:scale-110 shadow-lg"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a 
              href="https://www.tiktok.com/@atacadoasmar?_r=1&_t=ZS-94FvDAKlN5u" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-11 h-11 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-800 transition-all hover:scale-110 shadow-lg"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a 
              href="#"
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-11 h-11 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-all hover:scale-110 shadow-lg"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <Link 
              to="/vendedores/"
              className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-[#128C7E] transition-all hover:scale-110 shadow-lg"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-accent" />
              <p className="text-sm text-muted-foreground">
                {t('securePayment')}
              </p>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © {currentYear} {storeInfo.store_name}. {t('allRightsReserved')}
            </p>
            <div className="flex gap-4">
              <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                {t('terms')}
              </Link>
              <Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                {t('privacy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default StoreFooter;
